import { useEffect, useMemo, useState } from "react";
import { DatePicker, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { CalendarOutlined, CreditCardOutlined } from "@ant-design/icons";
import { CREATE_jwel } from "../../Config/Config";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";

// Shared Inventory-style components — same ones used by BankStatement.jsx
// and the Outstanding Customers/Dealers screens. Adjust the path if your
// project's shared folder differs.
import styles from "../Pages/shared/Inventory.module.css";
import InventoryPageHeader from "../Pages/shared/InventoryPageHeader";
import InventoryTable from "../Pages/shared/InventoryTable";

const { RangePicker } = DatePicker;

/* ─── filter box theme palette (same as BankStatement / URD) ────────────── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ─── amount chip, same visual family as BankStatement's Credit/Debit chips ─── */
const amountChipStyle = (kind) => {
  const palette = {
    paid: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    net: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  }[kind];

  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12.5,
    fontWeight: 700,
    background: palette.bg,
    color: palette.color,
    border: `1px solid ${palette.border}`,
  };
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "custname", label: "Customer" },
  { key: "Narration", label: "Narration" },
];
const secondaryFields = [
  { key: "Paid", label: "Paid" },
  { key: "NETPAID", label: "Net Paid" },
  { key: "BillAmt", label: "Bill Amount" },
];

const PaymentReciept = () => {
  const [rawData, setRawData] = useState([]);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");

  /* ---- data / API logic (unchanged endpoint + query params/format) ---- */
  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    setLoading(true);
    setTableVisible(false);

    const fromDate = dayjs(dates[0]).format("MM/DD/YYYY");
    const toDate = dayjs(dates[1]).format("MM/DD/YYYY");

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetPatymentRegisterData?fromDate=${fromDate}&toDate=${toDate}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        const calculatedData = detailsData.map((item, index) => ({
          ...item,
          key: index + 1,
          serialNo: index + 1,
        }));
        setRawData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching payment receipt details:", error);
      })
      .finally(() => setLoading(false));
  }, [dates]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    { title: "Receipt No", dataIndex: "receiptno", key: "receiptno" },
    {
      title: "Receipt Date",
      dataIndex: "receiptdate",
      key: "receiptdate",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    { title: "Customer Name", dataIndex: "custname", key: "custname" },
    { title: "Area", dataIndex: "Area", key: "Area" },
    { title: "Narration", dataIndex: "Narration", key: "Narration" },
    {
      title: "Mode",
      dataIndex: "MODE",
      key: "MODE",
      align: "center",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    { title: "Less", dataIndex: "Less", key: "Less", align: "right" },
    { title: "LCOMM", dataIndex: "LCOMM", key: "LCOMM", align: "right" },
    { title: "Cheque No", dataIndex: "CHEQUENO", key: "CHEQUENO" },
    {
      title: "Paid",
      dataIndex: "Paid",
      key: "Paid",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("paid")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Net Paid",
      dataIndex: "NETPAID",
      key: "NETPAID",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("net")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Bill Amount",
      dataIndex: "BillAmt",
      key: "BillAmt",
      align: "right",
      render: (value) => (
        <strong style={{ color: "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ];

  const totals = useMemo(() => {
    return rawData.reduce(
      (acc, item) => {
        acc.paid += Number(item.Paid || 0);
        acc.netPaid += Number(item.NETPAID || 0);
        acc.billAmt += Number(item.BillAmt || 0);
        return acc;
      },
      { paid: 0, netPaid: 0, billAmt: 0 },
    );
  }, [rawData]);

  const formattedData = useMemo(
    () =>
      rawData.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        Paid: Number(item.Paid || 0),
        NETPAID: Number(item.NETPAID || 0),
        BillAmt: Number(item.BillAmt || 0),
      })),
    [rawData],
  );

  const exportData = [
    ...formattedData.map((item) => ({
      ...item,
      receiptdate: item?.receiptdate
        ? dayjs(item.receiptdate).format("DD/MMM/YYYY")
        : "",
      Paid: item.Paid.toFixed(2),
      NETPAID: item.NETPAID.toFixed(2),
      BillAmt: item.BillAmt.toFixed(2),
    })),
    {
      serialNo: "Total",
      receiptno: "",
      receiptdate: "",
      custname: "",
      Area: "",
      Narration: "",
      MODE: "",
      Less: "",
      LCOMM: "",
      CHEQUENO: "",
      Paid: totals.paid.toFixed(2),
      NETPAID: totals.netPaid.toFixed(2),
      BillAmt: totals.billAmt.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <style>{`
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Payment Receipt Register"
        subtitle="Accounts · Payment Receipt"
        icon={<CreditCardOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={rawData.length}
        visible={rawData.length > 0}
        stats={[
          { label: "Records", value: rawData.length, color: "blue" },
          { label: "Paid", value: totals.paid.toFixed(2), color: "green" },
          {
            label: "Net Paid",
            value: totals.netPaid.toFixed(2),
            color: "teal",
          },
          {
            label: "Bill Amount",
            value: totals.billAmt.toFixed(2),
            color: "amber",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="PaymentReciept"
            totals={{
              totalDebit: totals.paid.toFixed(2),
              totalCredit: totals.netPaid.toFixed(2),
              totalBalance: totals.billAmt.toFixed(2),
            }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="pr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No payment receipts found for the selected date range"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row
            style={{ background: "#f5f5f5", fontWeight: 600 }}
          >
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.paid.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.netPaid.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.billAmt.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

export default PaymentReciept;